import { ChangeEvent, FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { FormCheckbox, FormGroup, FormHelpText, FormSelect } from '@spotify-internal/encore-web';
import { all } from '@spotify-internal/market';
import { filter } from 'lodash';

import {
  CLAIM_DETAILS,
  withFullTranslationKey,
} from 'components/ContentReporting/ContentLegalReporting/content-legal-reporting-config';
import { getFieldValid, getFieldValue } from 'components/ContentReporting/form-utils';
import { FieldOnChangeEvent, FormData } from 'containers/ContentReporting/ContentLegalReporting/utils/types';

type TerritoriesFieldsProps = {
  formData: FormData;
  handleFieldChange: (fieldChangeEvent: FieldOnChangeEvent) => void;
  territoryList: string[];
  isOtherTerritorySelected: boolean;
  forceValidation: boolean;
};

const OTHER_TERRITORIES_DELIMITER = ',';
const OTHER_TERRITORIES_LIMIT = 244;

const TerritoriesFields: FunctionComponent<TerritoriesFieldsProps> = props => {
  const { formData, handleFieldChange, territoryList, isOtherTerritorySelected } = props;

  const { t, i18n } = useTranslation(['infringement_claim_form', 'user_country']);

  const fieldValue = getFieldValue(formData);
  const fieldValid = getFieldValid(formData);

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const currentTerritories = fieldValue(CLAIM_DETAILS.fields.CLAIM_TERRITORIES);
    const newTerritories = {
      ...currentTerritories,
      [event.target.name]: event.target.checked,
    };

    handleFieldChange({
      fieldIdentifier: CLAIM_DETAILS.fields.CLAIM_TERRITORIES,
      value: newTerritories,
      valid: Object.values(newTerritories).some(x => !!x),
    });
  };

  const handleOnSelectTerritoriesChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    const selectedTerritories: string[] = !fieldValue(CLAIM_DETAILS.fields.CLAIM_OTHER_TERRITORIES)
      ? []
      : fieldValue(CLAIM_DETAILS.fields.CLAIM_OTHER_TERRITORIES).split(OTHER_TERRITORIES_DELIMITER);

    const countriesSelected = selectedTerritories.includes(value)
      ? filter(selectedTerritories, territory => territory !== value)
      : [...selectedTerritories, value];

    countriesSelected.sort();
    const otherTerritoriesValue = countriesSelected.join(OTHER_TERRITORIES_DELIMITER);

    handleFieldChange({
      fieldIdentifier: CLAIM_DETAILS.fields.CLAIM_OTHER_TERRITORIES,
      value: otherTerritoriesValue,
      valid: otherTerritoriesValue.length <= OTHER_TERRITORIES_LIMIT,
    });
  };

  const getValidCountries = () => {
    return all
      .filter((value: string) => i18n.exists(`user_country_${value}`, { ns: 'user_country' }))
      .map(value => ({ isoCode: value, name: t(`user_country_${value}`, { ns: 'user_country' }) }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  return (
    <FormGroup key="territory-checkboxes" label={t('territories')} indicator="required">
      {territoryList.map(territory => (
        <FormCheckbox
          id={territory}
          key={territory}
          name={territory}
          onChange={handleOnChange}
          checked={
            !!fieldValue(CLAIM_DETAILS.fields.CLAIM_TERRITORIES) &&
            !!fieldValue(CLAIM_DETAILS.fields.CLAIM_TERRITORIES)[territory]
          }
        >
          {t(withFullTranslationKey(territory))}
        </FormCheckbox>
      ))}
      {!fieldValid(CLAIM_DETAILS.fields.CLAIM_TERRITORIES) && (
        <FormHelpText error>{t('territory_selection_required')}</FormHelpText>
      )}
      {isOtherTerritorySelected && (
        <FormGroup
          label={t(CLAIM_DETAILS.labels.CLAIM_OTHER_TERRITORIES)}
          labelFor={CLAIM_DETAILS.fields.CLAIM_OTHER_TERRITORIES}
        >
          <FormSelect
            id={CLAIM_DETAILS.fields.CLAIM_OTHER_TERRITORIES}
            value={(fieldValue(CLAIM_DETAILS.fields.CLAIM_OTHER_TERRITORIES) || '').split(OTHER_TERRITORIES_DELIMITER)}
            error={!fieldValid(CLAIM_DETAILS.fields.CLAIM_OTHER_TERRITORIES)}
            onChange={handleOnSelectTerritoriesChange}
            multiple
          >
            {getValidCountries().map(validCountry => (
              <option key={validCountry.isoCode} value={validCountry.isoCode}>
                {validCountry.name}
              </option>
            ))}
          </FormSelect>
          {!fieldValid(CLAIM_DETAILS.fields.CLAIM_OTHER_TERRITORIES) && (
            <FormHelpText error>{t('other_territories_error')}</FormHelpText>
          )}
        </FormGroup>
      )}
    </FormGroup>
  );
};

export default TerritoriesFields;
